import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import Modules from 'shared_modules/Modules';

export default class Helpers {
  /**
   * Registers commonly used Handlebars helpers.
   */
  static register() {
    window.Handlebars.registerHelper('money', _money);
    window.Handlebars.registerHelper('max', _max);
    window.Handlebars.registerHelper('min', _min);
    window.Handlebars.registerHelper('eq', _equal);
    window.Handlebars.registerHelper('neg', _negative);
    window.Handlebars.registerHelper('trimWhitespace', _trimWhitespace);
    window.Handlebars.registerHelper('toLowerCase', _toLowerCase);
    window.Handlebars.registerHelper('hasModule', _hasModule);
  }
}

/**
 * Formats an amount into a monetary amount including the currency symbol.
 *
 * @param amount {string|number|Decimal}    The amount to formatted into money
 * @param withSymbol {boolean}              When set to false will suppress adding the currency symbol
 * @returns {Handlebars.SafeString}         The localized monetary amount as a string
 * @private
 */
function _money(amount, withSymbol = true) {
  return new window.Handlebars.SafeString(Format.formatMoney(amount, 0, withSymbol));
}

/**
 * Given two values, returns the maximum value.
 *
 * @param lvalue {string|number|Decimal}    The lvalue
 * @param rvalue {string|number|Decimal}    The rvalue
 * @private
 */
function _max(lvalue, rvalue) {
  return new window.Handlebars.SafeString(Decimal.max(lvalue, rvalue));
}

/**
 * Given two values, returns the minimum value.
 *
 * @param lvalue {string|number|Decimal}    The lvalue
 * @param rvalue {string|number|Decimal}    The rvalue
 * @private
 */
function _min(lvalue, rvalue) {
  return new window.Handlebars.SafeString(Decimal.min(lvalue, rvalue));
}

/**
 * Given two values, returns whether the lvalue is equal to the rvalue
 *
 * @param lvalue {*}    The lvalue
 * @param rvalue {*}    The rvalue
 *
 * @returns {boolean}
 * @private
 */
function _equal(lvalue, rvalue) {
  let isEqual = false;

  try {
    if (typeof rvalue === 'string') {
      let value1 = lvalue.toLowerCase();
      let value2 = rvalue.toLowerCase();

      isEqual = value1 === value2;
    } else {
      // need to handle numbers specially for
      // computability with decimal precision values
      let value1 = Format.parseDecimal(lvalue);
      let value2 = Format.parseDecimal(rvalue);

      isEqual = value1.isEqualTo(value2);
    }
  } catch (error) {
    isEqual = lvalue === rvalue;
  }

  return isEqual;
}

/**
 * Negates the value specified.
 *
 * @param value
 * @returns {string|number|Decimal}     A number to be negated
 * @private
 */
function _negative(value) {
  let negativeValue = null;

  try {
    negativeValue = Format.parseDecimal(value).negated();
  } catch (error) {
    negativeValue = Format.parseDecimal(0);
  }

  return negativeValue;
}

/**
 * Removes spaces in a value string so that it can be used as a variable name
 *
 * @param value        The String to have spaces removed from
 *
 * @returns {string}
 * @private
 */
function _trimWhitespace(value) {
  let trimmedString = '';
  if (value && value.toString()) {
    trimmedString = value.replace(/\s/g, '');
  }

  return trimmedString;
}

/**
 * Converts a string to lower case
 *
 * @param value {string}    The value to convert to lower case
 * @returns {Handlebars.SafeString}
 * @private
 */
function _toLowerCase(value) {
  let lowerCaseString = '';
  if (value) {
    lowerCaseString = new window.Handlebars.SafeString(value.toLowerCase());
  }

  return lowerCaseString;
}

/**
 * Returns whether a module is enabled or not
 *
 * @param moduleName {ModuleName} The name of the module
 * @returns {boolean}
 * @private
 */
function _hasModule(moduleName) {
  return Modules.hasModule(moduleName);
}
